/**
 * 데모 데이터
 * 
 * 비로그인 사용자에게 보여줄 예시 입력 데이터 및 결과 데이터
 * 5개의 샘플 중 랜덤하게 선택됨
 */

import type { ReleaseInput, ReleaseDecision, SuggestedTestCase } from './types';

// ============================================
// 샘플 1: 모바일 앱 다크모드 지원 (Conditional, 78점)
// ============================================
// 샘플 1 테스트케이스 (한국어)
const SAMPLE_1_TESTCASES_KO: SuggestedTestCase[] = [
  // 핵심 테스트 케이스 (Core)
  { id: 'TC-001', category: 'core', title: '다크모드 수동 전환', description: '설정에서 다크모드를 수동으로 켜고 끌 수 있는지 확인', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-002', category: 'core', title: '시스템 테마 자동 연동', description: '기기 설정 변경 시 앱 테마가 자동으로 연동되는지 확인', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-003', category: 'core', title: '전체 화면 다크모드 적용', description: '모든 주요 화면에서 다크모드가 올바르게 적용되는지 확인', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-004', category: 'core', title: '텍스트 가독성', description: '다크모드에서 모든 텍스트의 가독성이 확보되는지 확인', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-005', category: 'core', title: '아이콘/이미지 다크모드 대응', description: '아이콘과 이미지가 다크모드에 맞게 표시되는지 확인', priority: 'medium', status: 'pass', source: 'planning' },
  // 엣지 케이스 (Edge)
  { id: 'TC-006', category: 'edge', title: '커스텀 테마 충돌 확인', description: '기존 커스텀 테마 설정과 다크모드 간 충돌 여부 확인', priority: 'high', status: 'pass', source: 'risk' },
  { id: 'TC-007', category: 'edge', title: 'iOS 13 미만 동작', description: 'iOS 13 미만 기기에서 앱 정상 동작 확인 (다크모드 미지원 안내)', priority: 'medium', status: 'pass', source: 'risk' },
  { id: 'TC-008', category: 'edge', title: '외부 SDK 화면 확인', description: '다크모드 미지원 외부 SDK 화면의 폴백 UI 확인', priority: 'high', status: 'n/t', source: 'risk' },
  { id: 'TC-009', category: 'edge', title: '저전력 모드에서 테마 전환', description: '저전력 모드 활성화 상태에서 다크모드 전환 테스트', priority: 'medium', status: 'n/t', source: 'risk' },
  { id: 'TC-010', category: 'edge', title: '실시간 테마 전환 중 상태 유지', description: '앱 사용 중 테마 변경 시 현재 화면 상태가 유지되는지 확인', priority: 'medium', status: 'n/t', source: 'planning' },
  { id: 'TC-011', category: 'edge', title: '오프라인 상태에서 테마 저장', description: '네트워크 오프라인 상태에서 테마 설정이 로컬에 저장되는지 확인', priority: 'low', status: 'n/t', source: 'risk' },
  { id: 'TC-012', category: 'edge', title: '앱 재시작 후 테마 유지', description: '앱 종료 후 재시작 시 다크모드 설정이 유지되는지 확인', priority: 'medium', status: 'pass', source: 'planning' },
];

// 샘플 1 테스트케이스 (영어)
const SAMPLE_1_TESTCASES_EN: SuggestedTestCase[] = [
  // Core Test Cases
  { id: 'TC-001', category: 'core', title: 'Manual Dark Mode Toggle', description: 'Verify dark mode can be manually turned on/off in settings', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-002', category: 'core', title: 'System Theme Auto Sync', description: 'Verify app theme automatically syncs with device settings', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-003', category: 'core', title: 'Full Screen Dark Mode', description: 'Verify dark mode applies correctly to all main screens', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-004', category: 'core', title: 'Text Readability', description: 'Verify all text is readable in dark mode', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-005', category: 'core', title: 'Icon/Image Dark Mode Support', description: 'Verify icons and images display properly in dark mode', priority: 'medium', status: 'pass', source: 'planning' },
  // Edge Cases
  { id: 'TC-006', category: 'edge', title: 'Custom Theme Conflict Check', description: 'Verify no conflicts between existing custom themes and dark mode', priority: 'high', status: 'pass', source: 'risk' },
  { id: 'TC-007', category: 'edge', title: 'iOS 13 Below Behavior', description: 'Verify app works on iOS 13 below devices (dark mode unsupported notice)', priority: 'medium', status: 'pass', source: 'risk' },
  { id: 'TC-008', category: 'edge', title: 'External SDK Screen Check', description: 'Verify fallback UI for external SDK screens that don\'t support dark mode', priority: 'high', status: 'n/t', source: 'risk' },
  { id: 'TC-009', category: 'edge', title: 'Theme Switch in Low Power Mode', description: 'Test dark mode switching with low power mode enabled', priority: 'medium', status: 'n/t', source: 'risk' },
  { id: 'TC-010', category: 'edge', title: 'State Preservation During Theme Switch', description: 'Verify current screen state is preserved when theme changes during use', priority: 'medium', status: 'n/t', source: 'planning' },
  { id: 'TC-011', category: 'edge', title: 'Theme Save in Offline', description: 'Verify theme settings are saved locally in offline state', priority: 'low', status: 'n/t', source: 'risk' },
  { id: 'TC-012', category: 'edge', title: 'Theme Persistence After Restart', description: 'Verify dark mode setting persists after app restart', priority: 'medium', status: 'pass', source: 'planning' },
];

const SAMPLE_1_INPUT_KO: ReleaseInput = {
  feature_name: '모바일 앱 다크모드 지원',
  release_date: '2025-01-15',
  planning_intent: [
    '사용자 눈 피로도 감소 (야간 사용성 개선)',
    '시스템 테마 연동으로 UX 일관성 확보',
    '배터리 소모량 10% 감소 (OLED 기기)',
  ],
  risks: [
    '기존 커스텀 테마 사용자 설정 충돌 가능성',
    'iOS 13 미만 미지원 (사용자 5% 해당)',
    '일부 외부 SDK 다크모드 미지원',
  ],
  qa_status: {
    '핵심 시나리오': '검증 완료',
    '엣지 케이스': '일부 검증',
  },
  suggested_testcases: SAMPLE_1_TESTCASES_KO,
  lang: 'ko',
};

const SAMPLE_1_INPUT_EN: ReleaseInput = {
  feature_name: 'Mobile App Dark Mode Support',
  release_date: '2025-01-15',
  planning_intent: [
    'Reduce user eye fatigue (improve night usability)',
    'Ensure UX consistency with system theme integration',
    'Reduce battery consumption by 10% (OLED devices)',
  ],
  risks: [
    'Potential conflict with existing custom theme settings',
    'iOS 13 and below not supported (affects 5% of users)',
    'Some external SDKs do not support dark mode',
  ],
  qa_status: {
    'Core Scenario': 'Verified',
    'Edge Cases': 'Partially Verified',
  },
  suggested_testcases: SAMPLE_1_TESTCASES_EN,
  lang: 'en',
};

const SAMPLE_1_RESULT_KO: ReleaseDecision = {
  feature_name: '모바일 앱 다크모드 지원',
  release_date: '2025-01-15',
  confidence_score: 78,
  status: 'conditional',
  status_label: '조건부 출시',
  breakdown: [
    {
      category: 'Planning',
      category_ko: '기획 완성도',
      score: 23,
      max_score: 25,
      feedback: '기획 의도가 명확하고 사용자 가치가 잘 정의되어 있습니다. KPI 측정 방안이 구체적이면 더 좋습니다.',
    },
    {
      category: 'Risk',
      category_ko: '리스크 관리',
      score: 18,
      max_score: 25,
      feedback: '외부 SDK 호환성 리스크에 대한 대응 계획이 필요합니다. iOS 13 미만 사용자 마이그레이션 전략을 수립하세요.',
    },
    {
      category: 'QA',
      category_ko: 'QA 검증',
      score: 20,
      max_score: 25,
      feedback: '핵심 시나리오는 완벽히 검증되었으나, 엣지 케이스 4건의 추가 검증이 필요합니다.',
    },
    {
      category: 'Readiness',
      category_ko: '출시 준비도',
      score: 17,
      max_score: 25,
      feedback: '롤백 계획과 모니터링 지표를 사전에 준비하면 안전한 출시가 가능합니다.',
    },
  ],
  blockers: [],
  recommendations: [
    '엣지 케이스 4건 추가 검증 완료',
    '외부 SDK 다크모드 대응 확인',
    'iOS 13 미만 사용자 대상 공지 준비',
  ],
  structured_recommendations: [
    {
      title: '엣지 케이스 검증 완료',
      description: '미검증 엣지 케이스 4건에 대한 테스트를 완료해야 합니다. 특히 저전력 모드와 테마 전환 시나리오를 검증하세요.',
      priority: 'high',
      category: 'qa',
      action_items: [
        '저전력 모드에서 다크모드 전환 테스트',
        '시스템 테마 변경 시 앱 반응 테스트',
        '메모리 부족 상황에서 테마 렌더링 테스트',
        '네트워크 오프라인 상태에서 테마 저장 테스트',
      ],
      deadline: '출시 2일 전',
      owner: 'QA팀',
    },
    {
      title: '외부 SDK 호환성 확인',
      description: '다크모드를 지원하지 않는 외부 SDK에 대한 폴백 처리가 필요합니다.',
      priority: 'medium',
      category: 'risk',
      action_items: [
        '미지원 SDK 목록 정리',
        '폴백 UI 디자인 적용',
        'SDK 업데이트 일정 확인',
      ],
      deadline: '출시 3일 전',
      owner: '개발팀',
    },
    {
      title: '사용자 공지 준비',
      description: 'iOS 13 미만 사용자에게 다크모드 미지원 안내가 필요합니다.',
      priority: 'low',
      category: 'planning',
      action_items: [
        '인앱 공지 문구 작성',
        '지원 종료 예정 안내',
      ],
      deadline: '출시 당일',
      owner: '마케팅팀',
    },
  ],
  suggested_testcases: SAMPLE_1_TESTCASES_KO,
};

const SAMPLE_1_RESULT_EN: ReleaseDecision = {
  feature_name: 'Mobile App Dark Mode Support',
  release_date: '2025-01-15',
  confidence_score: 78,
  status: 'conditional',
  status_label: 'Conditional Release',
  breakdown: [
    {
      category: 'Planning',
      category_ko: '기획 완성도',
      score: 23,
      max_score: 25,
      feedback: 'Planning intent is clear and user value is well defined. More specific KPI measurement plans would be helpful.',
    },
    {
      category: 'Risk',
      category_ko: '리스크 관리',
      score: 18,
      max_score: 25,
      feedback: 'A response plan for external SDK compatibility risks is needed. Establish a migration strategy for users below iOS 13.',
    },
    {
      category: 'QA',
      category_ko: 'QA 검증',
      score: 20,
      max_score: 25,
      feedback: 'Core scenarios are fully verified, but 4 edge cases need additional verification.',
    },
    {
      category: 'Readiness',
      category_ko: '출시 준비도',
      score: 17,
      max_score: 25,
      feedback: 'Preparing a rollback plan and monitoring metrics in advance will enable a safe release.',
    },
  ],
  blockers: [],
  recommendations: [
    'Complete verification of 4 edge cases',
    'Confirm external SDK dark mode support',
    'Prepare notice for iOS 13 and below users',
  ],
  structured_recommendations: [
    {
      title: 'Complete Edge Case Verification',
      description: 'Testing for 4 unverified edge cases must be completed. Especially verify low power mode and theme switching scenarios.',
      priority: 'high',
      category: 'qa',
      action_items: [
        'Test dark mode switching in low power mode',
        'Test app response when system theme changes',
        'Test theme rendering in low memory situations',
        'Test theme saving in offline network state',
      ],
      deadline: '2 days before release',
      owner: 'QA Team',
    },
    {
      title: 'Verify External SDK Compatibility',
      description: 'Fallback handling is needed for external SDKs that do not support dark mode.',
      priority: 'medium',
      category: 'risk',
      action_items: [
        'List unsupported SDKs',
        'Apply fallback UI design',
        'Check SDK update schedule',
      ],
      deadline: '3 days before release',
      owner: 'Dev Team',
    },
    {
      title: 'Prepare User Notice',
      description: 'Users on iOS 13 and below need to be notified about dark mode unavailability.',
      priority: 'low',
      category: 'planning',
      action_items: [
        'Write in-app notice copy',
        'Announce end of support schedule',
      ],
      deadline: 'Release day',
      owner: 'Marketing Team',
    },
  ],
  suggested_testcases: SAMPLE_1_TESTCASES_EN,
};

// ============================================
// 샘플 2: 결제 시스템 업그레이드 (Go, 92점)
// ============================================
// 샘플 2 테스트케이스 (한국어)
const SAMPLE_2_TESTCASES_KO: SuggestedTestCase[] = [
  // 핵심 테스트 케이스 (Core)
  { id: 'TC-001', category: 'core', title: '신용카드 일반 결제', description: '국내 신용카드로 정상 결제 플로우 검증', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-002', category: 'core', title: '해외 카드 결제', description: '해외 발급 카드(Visa, Mastercard)로 결제 가능 여부 확인', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-003', category: 'core', title: '결제 취소/환불 처리', description: '결제 완료 후 전액 환불 및 부분 환불 처리 검증', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-004', category: 'core', title: '결제 내역 조회', description: '사용자 결제 내역 정상 조회 및 표시 확인', priority: 'medium', status: 'pass', source: 'planning' },
  { id: 'TC-005', category: 'core', title: '결제 실패 처리', description: '잔액 부족, 한도 초과 등 결제 실패 시 에러 핸들링 확인', priority: 'high', status: 'pass', source: 'planning' },
  // 엣지 케이스 (Edge)
  { id: 'TC-006', category: 'edge', title: '동시 다건 결제 처리', description: '1000 TPS 동시 결제 요청 시 정상 처리 검증', priority: 'high', status: 'pass', source: 'risk' },
  { id: 'TC-007', category: 'edge', title: 'API 타임아웃 복구', description: 'PG사 API 타임아웃 발생 시 재시도 및 복구 로직 검증', priority: 'high', status: 'pass', source: 'risk' },
  { id: 'TC-008', category: 'edge', title: '기존 결제 데이터 조회', description: '마이그레이션된 기존 결제 데이터 정상 조회 확인', priority: 'medium', status: 'pass', source: 'risk' },
  { id: 'TC-009', category: 'edge', title: '중복 결제 방지', description: '동일 주문에 대한 중복 결제 요청 차단 확인', priority: 'high', status: 'pass', source: 'risk' },
  { id: 'TC-010', category: 'edge', title: '네트워크 단절 시 처리', description: '결제 진행 중 네트워크 단절 시 트랜잭션 롤백 확인', priority: 'medium', status: 'pass', source: 'risk' },
];

// 샘플 2 테스트케이스 (영어)
const SAMPLE_2_TESTCASES_EN: SuggestedTestCase[] = [
  // Core Test Cases
  { id: 'TC-001', category: 'core', title: 'Credit Card Payment', description: 'Verify normal payment flow with domestic credit card', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-002', category: 'core', title: 'International Card Payment', description: 'Verify payment with international cards (Visa, Mastercard)', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-003', category: 'core', title: 'Payment Cancel/Refund', description: 'Verify full and partial refund processing after payment', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-004', category: 'core', title: 'Payment History Query', description: 'Verify user payment history display and retrieval', priority: 'medium', status: 'pass', source: 'planning' },
  { id: 'TC-005', category: 'core', title: 'Payment Failure Handling', description: 'Verify error handling for insufficient balance, limit exceeded', priority: 'high', status: 'pass', source: 'planning' },
  // Edge Cases
  { id: 'TC-006', category: 'edge', title: 'Concurrent Payment Processing', description: 'Verify 1000 TPS concurrent payment requests processed correctly', priority: 'high', status: 'pass', source: 'risk' },
  { id: 'TC-007', category: 'edge', title: 'API Timeout Recovery', description: 'Verify retry and recovery logic on PG API timeout', priority: 'high', status: 'pass', source: 'risk' },
  { id: 'TC-008', category: 'edge', title: 'Migrated Data Query', description: 'Verify migrated payment data is correctly retrievable', priority: 'medium', status: 'pass', source: 'risk' },
  { id: 'TC-009', category: 'edge', title: 'Duplicate Payment Prevention', description: 'Verify duplicate payment requests for same order are blocked', priority: 'high', status: 'pass', source: 'risk' },
  { id: 'TC-010', category: 'edge', title: 'Network Disconnection Handling', description: 'Verify transaction rollback on network disconnection during payment', priority: 'medium', status: 'pass', source: 'risk' },
];

const SAMPLE_2_INPUT_KO: ReleaseInput = {
  feature_name: '결제 시스템 PG사 업그레이드',
  release_date: '2025-02-01',
  planning_intent: [
    '결제 성공률 3% 향상 (신규 PG사 안정성)',
    '해외 카드 결제 지원 확대 (20개국 추가)',
    '결제 수수료 0.5% 절감',
  ],
  risks: [
    '기존 결제 데이터 마이그레이션 (100만 건)',
    '간헐적 타임아웃 가능성 (신규 API 안정화 기간)',
  ],
  qa_status: {
    '결제 플로우': '검증 완료',
    '환불 처리': '검증 완료',
    '동시성 테스트': '검증 완료',
  },
  suggested_testcases: SAMPLE_2_TESTCASES_KO,
  lang: 'ko',
};

const SAMPLE_2_INPUT_EN: ReleaseInput = {
  feature_name: 'Payment System PG Upgrade',
  release_date: '2025-02-01',
  planning_intent: [
    'Improve payment success rate by 3% (new PG stability)',
    'Expand international card payment support (20 countries added)',
    'Reduce payment fees by 0.5%',
  ],
  risks: [
    'Existing payment data migration (1 million records)',
    'Potential intermittent timeouts (new API stabilization period)',
  ],
  qa_status: {
    'Payment Flow': 'Verified',
    'Refund Process': 'Verified',
    'Concurrency Test': 'Verified',
  },
  suggested_testcases: SAMPLE_2_TESTCASES_EN,
  lang: 'en',
};

const SAMPLE_2_RESULT_KO: ReleaseDecision = {
  feature_name: '결제 시스템 PG사 업그레이드',
  release_date: '2025-02-01',
  confidence_score: 92,
  status: 'go',
  status_label: '출시 승인',
  breakdown: [
    {
      category: 'Planning',
      category_ko: '기획 완성도',
      score: 24,
      max_score: 25,
      feedback: '비즈니스 임팩트와 사용자 가치가 명확하게 정량화되어 있습니다. 훌륭한 기획입니다.',
    },
    {
      category: 'Risk',
      category_ko: '리스크 관리',
      score: 22,
      max_score: 25,
      feedback: '마이그레이션 리스크에 대한 롤백 계획이 수립되어 있습니다. 모니터링 강화를 권장합니다.',
    },
    {
      category: 'QA',
      category_ko: 'QA 검증',
      score: 25,
      max_score: 25,
      feedback: '모든 핵심 시나리오와 부하 테스트가 완벽하게 검증되었습니다.',
    },
    {
      category: 'Readiness',
      category_ko: '출시 준비도',
      score: 21,
      max_score: 25,
      feedback: '출시 준비가 잘 되어 있습니다. 실시간 모니터링 대시보드 준비를 권장합니다.',
    },
  ],
  blockers: [],
  recommendations: [
    '출시 후 24시간 집중 모니터링 실시',
    'PG사 핫라인 연락처 공유',
  ],
  structured_recommendations: [
    {
      title: '출시 후 집중 모니터링',
      description: '신규 PG사 안정화를 위해 출시 후 24시간 동안 결제 지표를 집중 모니터링하세요.',
      priority: 'medium',
      category: 'readiness',
      action_items: [
        '실시간 결제 성공률 모니터링',
        '에러 로그 알림 설정',
        'PG사 담당자 대기 확인',
      ],
      deadline: '출시 직후',
      owner: '인프라팀',
    },
  ],
  suggested_testcases: SAMPLE_2_TESTCASES_KO,
};

const SAMPLE_2_RESULT_EN: ReleaseDecision = {
  feature_name: 'Payment System PG Upgrade',
  release_date: '2025-02-01',
  confidence_score: 92,
  status: 'go',
  status_label: 'Release Approved',
  breakdown: [
    {
      category: 'Planning',
      category_ko: '기획 완성도',
      score: 24,
      max_score: 25,
      feedback: 'Business impact and user value are clearly quantified. Excellent planning.',
    },
    {
      category: 'Risk',
      category_ko: '리스크 관리',
      score: 22,
      max_score: 25,
      feedback: 'Rollback plan for migration risks is established. Enhanced monitoring is recommended.',
    },
    {
      category: 'QA',
      category_ko: 'QA 검증',
      score: 25,
      max_score: 25,
      feedback: 'All core scenarios and load tests have been perfectly verified.',
    },
    {
      category: 'Readiness',
      category_ko: '출시 준비도',
      score: 21,
      max_score: 25,
      feedback: 'Release preparation is well done. Real-time monitoring dashboard preparation is recommended.',
    },
  ],
  blockers: [],
  recommendations: [
    'Conduct intensive monitoring for 24 hours post-release',
    'Share PG hotline contacts',
  ],
  structured_recommendations: [
    {
      title: 'Post-Release Intensive Monitoring',
      description: 'Intensively monitor payment metrics for 24 hours after release for new PG stabilization.',
      priority: 'medium',
      category: 'readiness',
      action_items: [
        'Real-time payment success rate monitoring',
        'Set up error log alerts',
        'Confirm PG representative on standby',
      ],
      deadline: 'Immediately after release',
      owner: 'Infrastructure Team',
    },
  ],
  suggested_testcases: SAMPLE_2_TESTCASES_EN,
};

// ============================================
// 샘플 3: 신규 API 서버 이전 (No Go, 45점)
// ============================================
// 샘플 3 테스트케이스 (한국어)
const SAMPLE_3_TESTCASES_KO: SuggestedTestCase[] = [
  // 핵심 테스트 케이스 (Core)
  { id: 'TC-001', category: 'core', title: 'API 엔드포인트 응답 검증', description: '모든 주요 API 엔드포인트가 정상 응답하는지 확인', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-002', category: 'core', title: '데이터베이스 연결', description: '신규 클라우드 환경에서 DB 연결 및 쿼리 정상 동작 확인', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-003', category: 'core', title: '인증/인가 로직', description: '사용자 인증 및 권한 검증 로직 정상 동작 확인', priority: 'high', status: 'n/t', source: 'planning' },
  { id: 'TC-004', category: 'core', title: '기본 CRUD 동작', description: '생성/조회/수정/삭제 기본 기능 정상 동작 확인', priority: 'high', status: 'pass', source: 'planning' },
  // 엣지 케이스 (Edge)
  { id: 'TC-005', category: 'edge', title: '데이터 정합성 검증', description: '마이그레이션 후 기존 데이터 정합성 확인', priority: 'high', status: 'n/t', source: 'risk' },
  { id: 'TC-006', category: 'edge', title: '외부 API 연동 테스트', description: '50개 외부 연동 API 정상 동작 확인', priority: 'high', status: 'fail', source: 'risk', memo: '30개만 테스트 완료 (60%)' },
  { id: 'TC-007', category: 'edge', title: '부하 테스트', description: '피크 트래픽 대비 성능 테스트 (1000 TPS)', priority: 'high', status: 'n/t', source: 'risk' },
  { id: 'TC-008', category: 'edge', title: '롤백 절차 검증', description: '장애 발생 시 원복 절차 및 소요 시간 검증', priority: 'high', status: 'n/t', source: 'risk' },
  { id: 'TC-009', category: 'edge', title: '보안 취약점 스캔', description: '신규 환경 보안 취약점 점검', priority: 'high', status: 'n/t', source: 'risk' },
  { id: 'TC-010', category: 'edge', title: '자동 스케일링 테스트', description: '트래픽 증가 시 자동 스케일 아웃 동작 확인', priority: 'medium', status: 'n/t', source: 'planning' },
];

// 샘플 3 테스트케이스 (영어)
const SAMPLE_3_TESTCASES_EN: SuggestedTestCase[] = [
  // Core Test Cases
  { id: 'TC-001', category: 'core', title: 'API Endpoint Response', description: 'Verify all major API endpoints respond correctly', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-002', category: 'core', title: 'Database Connection', description: 'Verify DB connection and query execution in new cloud environment', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-003', category: 'core', title: 'Auth/Authorization Logic', description: 'Verify user authentication and permission validation logic', priority: 'high', status: 'n/t', source: 'planning' },
  { id: 'TC-004', category: 'core', title: 'Basic CRUD Operations', description: 'Verify create/read/update/delete basic functions work correctly', priority: 'high', status: 'pass', source: 'planning' },
  // Edge Cases
  { id: 'TC-005', category: 'edge', title: 'Data Integrity Verification', description: 'Verify existing data integrity after migration', priority: 'high', status: 'n/t', source: 'risk' },
  { id: 'TC-006', category: 'edge', title: 'External API Integration Test', description: 'Verify 50 external API integrations work correctly', priority: 'high', status: 'fail', source: 'risk', memo: 'Only 30 tested (60%)' },
  { id: 'TC-007', category: 'edge', title: 'Load Test', description: 'Performance test for peak traffic (1000 TPS)', priority: 'high', status: 'n/t', source: 'risk' },
  { id: 'TC-008', category: 'edge', title: 'Rollback Procedure Verification', description: 'Verify rollback procedure and time on failure', priority: 'high', status: 'n/t', source: 'risk' },
  { id: 'TC-009', category: 'edge', title: 'Security Vulnerability Scan', description: 'Security vulnerability check for new environment', priority: 'high', status: 'n/t', source: 'risk' },
  { id: 'TC-010', category: 'edge', title: 'Auto Scaling Test', description: 'Verify auto scale-out on traffic increase', priority: 'medium', status: 'n/t', source: 'planning' },
];

const SAMPLE_3_INPUT_KO: ReleaseInput = {
  feature_name: '레거시 API 서버 클라우드 이전',
  release_date: '2025-01-20',
  planning_intent: [
    '인프라 비용 30% 절감',
    '자동 스케일링으로 트래픽 대응력 향상',
  ],
  risks: [
    '데이터 정합성 검증 미완료',
    '서비스 다운타임 4시간 예상',
    '외부 연동 API 50개 중 30개만 테스트 완료',
    '롤백 절차 미수립',
  ],
  qa_status: {
    '기능 테스트': '일부 검증',
    '성능 테스트': '미검증',
    '보안 테스트': '미검증',
  },
  suggested_testcases: SAMPLE_3_TESTCASES_KO,
  lang: 'ko',
};

const SAMPLE_3_INPUT_EN: ReleaseInput = {
  feature_name: 'Legacy API Server Cloud Migration',
  release_date: '2025-01-20',
  planning_intent: [
    'Reduce infrastructure costs by 30%',
    'Improve traffic handling with auto-scaling',
  ],
  risks: [
    'Data integrity verification incomplete',
    'Expected service downtime of 4 hours',
    'Only 30 of 50 external API integrations tested',
    'Rollback procedure not established',
  ],
  qa_status: {
    'Functional Test': 'Partially Verified',
    'Performance Test': 'Unverified',
    'Security Test': 'Unverified',
  },
  suggested_testcases: SAMPLE_3_TESTCASES_EN,
  lang: 'en',
};

const SAMPLE_3_RESULT_KO: ReleaseDecision = {
  feature_name: '레거시 API 서버 클라우드 이전',
  release_date: '2025-01-20',
  confidence_score: 45,
  status: 'no_go',
  status_label: '출시 불가',
  breakdown: [
    {
      category: 'Planning',
      category_ko: '기획 완성도',
      score: 15,
      max_score: 25,
      feedback: '비용 절감 목표는 있으나, 마이그레이션 세부 계획과 일정이 부족합니다.',
    },
    {
      category: 'Risk',
      category_ko: '리스크 관리',
      score: 8,
      max_score: 25,
      feedback: '롤백 계획이 없고, 외부 API 연동 테스트가 40%만 완료되어 치명적인 리스크가 존재합니다.',
    },
    {
      category: 'QA',
      category_ko: 'QA 검증',
      score: 10,
      max_score: 25,
      feedback: '기능 테스트 45%만 진행되었고, 성능/보안 테스트가 전혀 시작되지 않았습니다.',
    },
    {
      category: 'Readiness',
      category_ko: '출시 준비도',
      score: 12,
      max_score: 25,
      feedback: '4시간 다운타임은 서비스 SLA를 위반합니다. 무중단 마이그레이션 전략이 필요합니다.',
    },
  ],
  blockers: [
    '롤백 절차 미수립 - 장애 시 복구 불가',
    '성능 테스트 미진행 - 서비스 안정성 검증 불가',
    '보안 테스트 미진행 - 컴플라이언스 위반 가능',
  ],
  recommendations: [
    '롤백 절차 수립 및 검증',
    '성능/보안 테스트 완료',
    '외부 API 연동 테스트 100% 완료',
    '무중단 마이그레이션 전략 수립',
  ],
  structured_recommendations: [
    {
      title: '롤백 절차 수립',
      description: '마이그레이션 실패 시 즉시 복구할 수 있는 롤백 절차를 수립하고 검증해야 합니다.',
      priority: 'high',
      category: 'risk',
      action_items: [
        '롤백 시나리오 문서화',
        '롤백 스크립트 작성',
        '롤백 드릴 실행 및 검증',
        '롤백 소요 시간 측정',
      ],
      deadline: '출시 1주 전',
      owner: '인프라팀',
    },
    {
      title: '테스트 완료',
      description: '성능 테스트와 보안 테스트를 반드시 완료해야 합니다.',
      priority: 'high',
      category: 'qa',
      action_items: [
        '부하 테스트 시나리오 작성 및 실행',
        '보안 취약점 스캔 실행',
        '침투 테스트 진행',
      ],
      deadline: '출시 5일 전',
      owner: 'QA팀/보안팀',
    },
    {
      title: '무중단 마이그레이션 전략',
      description: '4시간 다운타임은 허용 불가합니다. Blue-Green 또는 Canary 배포 전략을 검토하세요.',
      priority: 'high',
      category: 'readiness',
      action_items: [
        'Blue-Green 배포 환경 구축',
        '트래픽 전환 자동화',
        '헬스체크 및 자동 롤백 설정',
      ],
      deadline: '출시 3일 전',
      owner: '인프라팀',
    },
  ],
  suggested_testcases: SAMPLE_3_TESTCASES_KO,
};

const SAMPLE_3_RESULT_EN: ReleaseDecision = {
  feature_name: 'Legacy API Server Cloud Migration',
  release_date: '2025-01-20',
  confidence_score: 45,
  status: 'no_go',
  status_label: 'Release Blocked',
  breakdown: [
    {
      category: 'Planning',
      category_ko: '기획 완성도',
      score: 15,
      max_score: 25,
      feedback: 'Cost reduction goals exist, but detailed migration plan and schedule are lacking.',
    },
    {
      category: 'Risk',
      category_ko: '리스크 관리',
      score: 8,
      max_score: 25,
      feedback: 'No rollback plan, and only 40% of external API integration tests completed - critical risks exist.',
    },
    {
      category: 'QA',
      category_ko: 'QA 검증',
      score: 10,
      max_score: 25,
      feedback: 'Only 45% of functional tests completed, and performance/security tests have not started at all.',
    },
    {
      category: 'Readiness',
      category_ko: '출시 준비도',
      score: 12,
      max_score: 25,
      feedback: '4-hour downtime violates service SLA. Zero-downtime migration strategy is needed.',
    },
  ],
  blockers: [
    'No rollback procedure - recovery impossible in case of failure',
    'No performance testing - cannot verify service stability',
    'No security testing - potential compliance violation',
  ],
  recommendations: [
    'Establish and verify rollback procedures',
    'Complete performance/security tests',
    'Complete 100% of external API integration tests',
    'Establish zero-downtime migration strategy',
  ],
  structured_recommendations: [
    {
      title: 'Establish Rollback Procedure',
      description: 'A rollback procedure that can immediately recover in case of migration failure must be established and verified.',
      priority: 'high',
      category: 'risk',
      action_items: [
        'Document rollback scenarios',
        'Write rollback scripts',
        'Execute and verify rollback drill',
        'Measure rollback time',
      ],
      deadline: '1 week before release',
      owner: 'Infrastructure Team',
    },
    {
      title: 'Complete Testing',
      description: 'Performance and security tests must be completed.',
      priority: 'high',
      category: 'qa',
      action_items: [
        'Write and execute load test scenarios',
        'Run security vulnerability scan',
        'Conduct penetration testing',
      ],
      deadline: '5 days before release',
      owner: 'QA Team/Security Team',
    },
    {
      title: 'Zero-Downtime Migration Strategy',
      description: '4-hour downtime is not acceptable. Review Blue-Green or Canary deployment strategies.',
      priority: 'high',
      category: 'readiness',
      action_items: [
        'Set up Blue-Green deployment environment',
        'Automate traffic switching',
        'Set up health checks and auto-rollback',
      ],
      deadline: '3 days before release',
      owner: 'Infrastructure Team',
    },
  ],
  suggested_testcases: SAMPLE_3_TESTCASES_EN,
};

// ============================================
// 샘플 4: 푸시 알림 시스템 개선 (Holding, 58점)
// ============================================
// 샘플 4 테스트케이스 (한국어)
const SAMPLE_4_TESTCASES_KO: SuggestedTestCase[] = [
  // 핵심 테스트 케이스 (Core)
  { id: 'TC-001', category: 'core', title: '푸시 알림 발송', description: '기본 푸시 알림이 정상적으로 발송되는지 확인', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-002', category: 'core', title: '알림 수신 및 표시', description: '사용자 기기에서 알림이 정상 수신되고 표시되는지 확인', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-003', category: 'core', title: '알림 클릭 시 딥링크', description: '알림 클릭 시 앱 내 목표 화면으로 이동하는지 확인', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-004', category: 'core', title: '일일 알림 제한', description: '일일 최대 3건 알림 제한 로직 정상 동작 확인', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-005', category: 'core', title: '알림 설정 ON/OFF', description: '사용자 알림 설정 변경이 정상 반영되는지 확인', priority: 'medium', status: 'pass', source: 'planning' },
  // 엣지 케이스 (Edge)
  { id: 'TC-006', category: 'edge', title: '개인화 추천 알림', description: '사용자 행동 기반 개인화 알림 콘텐츠 생성 검증', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-007', category: 'edge', title: 'ML 모델 정확도', description: 'ML 기반 추천 정확도 85% 이상 달성 여부 확인', priority: 'high', status: 'fail', source: 'risk', memo: '현재 75% - 목표 미달' },
  { id: 'TC-008', category: 'edge', title: '개인정보 동의 플로우', description: '개인정보 처리 동의 획득 및 관리 플로우 검증', priority: 'high', status: 'n/t', source: 'risk', memo: '법무 검토 대기 중' },
  { id: 'TC-009', category: 'edge', title: 'A/B 테스트 기능', description: '알림 A/B 테스트 생성 및 결과 측정 기능 검증', priority: 'medium', status: 'pass', source: 'planning' },
  { id: 'TC-010', category: 'edge', title: '기존 시스템 병행 운영', description: '신규 시스템과 기존 알림 시스템 간 충돌 없이 병행 운영 확인', priority: 'medium', status: 'n/t', source: 'risk' },
];

// 샘플 4 테스트케이스 (영어)
const SAMPLE_4_TESTCASES_EN: SuggestedTestCase[] = [
  // Core Test Cases
  { id: 'TC-001', category: 'core', title: 'Push Notification Send', description: 'Verify basic push notification is sent correctly', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-002', category: 'core', title: 'Notification Receive & Display', description: 'Verify notification is received and displayed on user device', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-003', category: 'core', title: 'Deep Link on Click', description: 'Verify clicking notification navigates to target screen in app', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-004', category: 'core', title: 'Daily Notification Limit', description: 'Verify daily max 3 notification limit logic works correctly', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-005', category: 'core', title: 'Notification Settings ON/OFF', description: 'Verify user notification settings changes are applied correctly', priority: 'medium', status: 'pass', source: 'planning' },
  // Edge Cases
  { id: 'TC-006', category: 'edge', title: 'Personalized Recommendation', description: 'Verify personalized notification content generation based on user behavior', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-007', category: 'edge', title: 'ML Model Accuracy', description: 'Verify ML-based recommendation accuracy reaches 85% or above', priority: 'high', status: 'fail', source: 'risk', memo: 'Currently 75% - below target' },
  { id: 'TC-008', category: 'edge', title: 'Privacy Consent Flow', description: 'Verify privacy consent acquisition and management flow', priority: 'high', status: 'n/t', source: 'risk', memo: 'Awaiting legal review' },
  { id: 'TC-009', category: 'edge', title: 'A/B Test Feature', description: 'Verify notification A/B test creation and result measurement', priority: 'medium', status: 'pass', source: 'planning' },
  { id: 'TC-010', category: 'edge', title: 'Legacy System Parallel Operation', description: 'Verify no conflicts between new and existing notification systems', priority: 'medium', status: 'n/t', source: 'risk' },
];

const SAMPLE_4_INPUT_KO: ReleaseInput = {
  feature_name: '개인화 푸시 알림 시스템 v2.0',
  release_date: '2025-01-25',
  planning_intent: [
    '사용자 행동 기반 개인화 알림 (클릭률 2배 향상 목표)',
    '알림 피로도 감소 (일일 최대 3건 제한)',
    'A/B 테스트 기능 내장',
  ],
  risks: [
    '개인정보 처리 동의 절차 법무 검토 중',
    '기존 알림 시스템과의 병행 운영 복잡성',
    'ML 모델 정확도 75% (목표 85% 미달)',
  ],
  qa_status: {
    '알림 발송': '검증 완료',
    '개인화 로직': '일부 검증',
    '동의 플로우': '미검증',
  },
  suggested_testcases: SAMPLE_4_TESTCASES_KO,
  lang: 'ko',
};

const SAMPLE_4_INPUT_EN: ReleaseInput = {
  feature_name: 'Personalized Push Notification System v2.0',
  release_date: '2025-01-25',
  planning_intent: [
    'Personalized notifications based on user behavior (target 2x CTR improvement)',
    'Reduce notification fatigue (max 3 per day limit)',
    'Built-in A/B testing capability',
  ],
  risks: [
    'Privacy consent process under legal review',
    'Complexity of parallel operation with existing notification system',
    'ML model accuracy 75% (below 85% target)',
  ],
  qa_status: {
    'Notification Delivery': 'Verified',
    'Personalization Logic': 'Partially Verified',
    'Consent Flow': 'Unverified',
  },
  suggested_testcases: SAMPLE_4_TESTCASES_EN,
  lang: 'en',
};

const SAMPLE_4_RESULT_KO: ReleaseDecision = {
  feature_name: '개인화 푸시 알림 시스템 v2.0',
  release_date: '2025-01-25',
  confidence_score: 58,
  status: 'holding',
  status_label: '보류',
  breakdown: [
    {
      category: 'Planning',
      category_ko: '기획 완성도',
      score: 20,
      max_score: 25,
      feedback: '기능 기획은 우수하나, 개인정보 처리 동의 플로우가 법무 검토 중입니다.',
    },
    {
      category: 'Risk',
      category_ko: '리스크 관리',
      score: 12,
      max_score: 25,
      feedback: 'ML 모델 정확도가 목표에 미달하고, 법무 리스크가 해소되지 않았습니다.',
    },
    {
      category: 'QA',
      category_ko: 'QA 검증',
      score: 14,
      max_score: 25,
      feedback: '핵심 발송 기능은 검증되었으나, 개인화 로직과 동의 플로우 테스트가 필요합니다.',
    },
    {
      category: 'Readiness',
      category_ko: '출시 준비도',
      score: 12,
      max_score: 25,
      feedback: '법무 검토 완료 후 동의 플로우 구현 및 테스트가 필요합니다.',
    },
  ],
  blockers: [
    '개인정보 처리 동의 절차 법무 검토 미완료',
  ],
  recommendations: [
    '법무 검토 완료 및 동의 플로우 확정',
    'ML 모델 정확도 개선 또는 임계값 조정',
    '개인화 로직 테스트 완료',
  ],
  structured_recommendations: [
    {
      title: '법무 검토 완료',
      description: '개인정보 처리 동의 절차에 대한 법무 검토를 우선 완료해야 합니다. 이것이 Blocker입니다.',
      priority: 'high',
      category: 'planning',
      action_items: [
        '법무팀 검토 일정 확인',
        '검토 결과에 따른 동의 플로우 설계',
        '개인정보 처리방침 업데이트',
      ],
      deadline: '출시 5일 전',
      owner: '법무팀/기획팀',
    },
    {
      title: 'ML 모델 개선',
      description: '현재 75% 정확도를 85% 이상으로 개선하거나, 낮은 정확도에서도 작동하도록 임계값을 조정하세요.',
      priority: 'medium',
      category: 'risk',
      action_items: [
        '추가 학습 데이터 수집',
        '모델 하이퍼파라미터 튜닝',
        '정확도 임계값 재검토',
      ],
      deadline: '출시 3일 전',
      owner: 'ML팀',
    },
  ],
  suggested_testcases: SAMPLE_4_TESTCASES_KO,
};

const SAMPLE_4_RESULT_EN: ReleaseDecision = {
  feature_name: 'Personalized Push Notification System v2.0',
  release_date: '2025-01-25',
  confidence_score: 58,
  status: 'holding',
  status_label: 'On Hold',
  breakdown: [
    {
      category: 'Planning',
      category_ko: '기획 완성도',
      score: 20,
      max_score: 25,
      feedback: 'Feature planning is excellent, but privacy consent flow is under legal review.',
    },
    {
      category: 'Risk',
      category_ko: '리스크 관리',
      score: 12,
      max_score: 25,
      feedback: 'ML model accuracy is below target, and legal risks are not resolved.',
    },
    {
      category: 'QA',
      category_ko: 'QA 검증',
      score: 14,
      max_score: 25,
      feedback: 'Core delivery function is verified, but personalization logic and consent flow tests are needed.',
    },
    {
      category: 'Readiness',
      category_ko: '출시 준비도',
      score: 12,
      max_score: 25,
      feedback: 'Consent flow implementation and testing needed after legal review completion.',
    },
  ],
  blockers: [
    'Privacy consent procedure legal review incomplete',
  ],
  recommendations: [
    'Complete legal review and finalize consent flow',
    'Improve ML model accuracy or adjust threshold',
    'Complete personalization logic testing',
  ],
  structured_recommendations: [
    {
      title: 'Complete Legal Review',
      description: 'Legal review of privacy consent procedure must be completed first. This is a blocker.',
      priority: 'high',
      category: 'planning',
      action_items: [
        'Confirm legal team review schedule',
        'Design consent flow based on review results',
        'Update privacy policy',
      ],
      deadline: '5 days before release',
      owner: 'Legal Team/Planning Team',
    },
    {
      title: 'Improve ML Model',
      description: 'Improve current 75% accuracy to 85% or above, or adjust thresholds to work with lower accuracy.',
      priority: 'medium',
      category: 'risk',
      action_items: [
        'Collect additional training data',
        'Tune model hyperparameters',
        'Re-evaluate accuracy threshold',
      ],
      deadline: '3 days before release',
      owner: 'ML Team',
    },
  ],
  suggested_testcases: SAMPLE_4_TESTCASES_EN,
};

// ============================================
// 샘플 5: 회원가입 프로세스 개선 (Conditional, 72점)
// ============================================
// 샘플 5 테스트케이스 (한국어)
const SAMPLE_5_TESTCASES_KO: SuggestedTestCase[] = [
  // 핵심 테스트 케이스 (Core)
  { id: 'TC-001', category: 'core', title: '카카오 로그인', description: '카카오 계정으로 로그인/회원가입 정상 동작 확인', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-002', category: 'core', title: '네이버 로그인', description: '네이버 계정으로 로그인/회원가입 정상 동작 확인', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-003', category: 'core', title: '구글 로그인', description: '구글 계정으로 로그인/회원가입 정상 동작 확인', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-004', category: 'core', title: '간소화된 회원가입 플로우', description: '2단계로 축소된 회원가입 프로세스 정상 동작 확인', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-005', category: 'core', title: '로그아웃', description: '소셜 로그인 후 정상 로그아웃 처리 확인', priority: 'medium', status: 'pass', source: 'planning' },
  // 엣지 케이스 (Edge)
  { id: 'TC-006', category: 'edge', title: '기존 회원 소셜 계정 연동', description: '기존 이메일 회원이 소셜 계정을 연동하는 플로우 검증', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-007', category: 'edge', title: '동일 이메일 계정 충돌 처리', description: '소셜 로그인 이메일과 기존 회원 이메일 충돌 시 처리 확인', priority: 'high', status: 'n/t', source: 'risk' },
  { id: 'TC-008', category: 'edge', title: '소셜 로그인 장애 대응', description: '소셜 로그인 서비스 장애 시 대체 로그인 경로 안내 확인', priority: 'high', status: 'n/t', source: 'risk' },
  { id: 'TC-009', category: 'edge', title: '다중 소셜 계정 연동', description: '한 계정에 여러 소셜 계정(카카오+네이버+구글) 연동 검증', priority: 'medium', status: 'n/t', source: 'planning' },
  { id: 'TC-010', category: 'edge', title: '연동 해제 및 재연동', description: '소셜 계정 연동 해제 후 재연동 정상 동작 확인', priority: 'medium', status: 'pass', source: 'planning' },
  { id: 'TC-011', category: 'edge', title: '토큰 갱신 처리', description: '소셜 로그인 토큰 만료 시 자동 갱신 처리 확인', priority: 'medium', status: 'pass', source: 'risk' },
  { id: 'TC-012', category: 'edge', title: '회원 탈퇴 시 연동 해제', description: '회원 탈퇴 시 모든 소셜 계정 연동 자동 해제 확인', priority: 'low', status: 'pass', source: 'risk' },
];

// 샘플 5 테스트케이스 (영어)
const SAMPLE_5_TESTCASES_EN: SuggestedTestCase[] = [
  // Core Test Cases
  { id: 'TC-001', category: 'core', title: 'Kakao Login', description: 'Verify login/signup with Kakao account works correctly', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-002', category: 'core', title: 'Naver Login', description: 'Verify login/signup with Naver account works correctly', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-003', category: 'core', title: 'Google Login', description: 'Verify login/signup with Google account works correctly', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-004', category: 'core', title: 'Simplified Signup Flow', description: 'Verify 2-step simplified signup process works correctly', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-005', category: 'core', title: 'Logout', description: 'Verify logout after social login works correctly', priority: 'medium', status: 'pass', source: 'planning' },
  // Edge Cases
  { id: 'TC-006', category: 'edge', title: 'Existing Member Social Link', description: 'Verify existing email member can link social account', priority: 'high', status: 'pass', source: 'planning' },
  { id: 'TC-007', category: 'edge', title: 'Same Email Account Conflict', description: 'Verify handling when social login email conflicts with existing member', priority: 'high', status: 'n/t', source: 'risk' },
  { id: 'TC-008', category: 'edge', title: 'Social Login Outage Response', description: 'Verify alternative login path is provided on social login service outage', priority: 'high', status: 'n/t', source: 'risk' },
  { id: 'TC-009', category: 'edge', title: 'Multiple Social Account Link', description: 'Verify linking multiple social accounts (Kakao+Naver+Google) to one account', priority: 'medium', status: 'n/t', source: 'planning' },
  { id: 'TC-010', category: 'edge', title: 'Unlink and Relink', description: 'Verify social account unlink and relink works correctly', priority: 'medium', status: 'pass', source: 'planning' },
  { id: 'TC-011', category: 'edge', title: 'Token Refresh Handling', description: 'Verify auto token refresh on social login token expiration', priority: 'medium', status: 'pass', source: 'risk' },
  { id: 'TC-012', category: 'edge', title: 'Account Deletion Unlink', description: 'Verify all social accounts are unlinked on member deletion', priority: 'low', status: 'pass', source: 'risk' },
];

const SAMPLE_5_INPUT_KO: ReleaseInput = {
  feature_name: '소셜 로그인 통합 및 회원가입 간소화',
  release_date: '2025-02-10',
  planning_intent: [
    '회원가입 전환율 40% 향상 (단계 축소: 5단계 → 2단계)',
    '카카오/네이버/구글 소셜 로그인 통합',
    '기존 회원 소셜 계정 연동 지원',
  ],
  risks: [
    '소셜 로그인 장애 시 대체 로그인 경로 필요',
    '기존 회원 소셜 연동 시 계정 충돌 가능성',
  ],
  qa_status: {
    '카카오 로그인': '검증 완료',
    '네이버 로그인': '검증 완료',
    '구글 로그인': '검증 완료',
    '계정 연동': '일부 검증',
  },
  suggested_testcases: SAMPLE_5_TESTCASES_KO,
  lang: 'ko',
};

const SAMPLE_5_INPUT_EN: ReleaseInput = {
  feature_name: 'Social Login Integration and Signup Simplification',
  release_date: '2025-02-10',
  planning_intent: [
    'Improve signup conversion rate by 40% (reduce steps: 5 → 2)',
    'Integrate Kakao/Naver/Google social login',
    'Support social account linking for existing members',
  ],
  risks: [
    'Alternative login path needed for social login outages',
    'Potential account conflicts when linking existing member social accounts',
  ],
  qa_status: {
    'Kakao Login': 'Verified',
    'Naver Login': 'Verified',
    'Google Login': 'Verified',
    'Account Linking': 'Partially Verified',
  },
  suggested_testcases: SAMPLE_5_TESTCASES_EN,
  lang: 'en',
};

const SAMPLE_5_RESULT_KO: ReleaseDecision = {
  feature_name: '소셜 로그인 통합 및 회원가입 간소화',
  release_date: '2025-02-10',
  confidence_score: 72,
  status: 'conditional',
  status_label: '조건부 출시',
  breakdown: [
    {
      category: 'Planning',
      category_ko: '기획 완성도',
      score: 22,
      max_score: 25,
      feedback: '전환율 향상 목표가 명확하고, 사용자 여정이 잘 설계되어 있습니다.',
    },
    {
      category: 'Risk',
      category_ko: '리스크 관리',
      score: 17,
      max_score: 25,
      feedback: '소셜 로그인 장애 대응 계획은 있으나, 계정 충돌 처리 로직 검증이 필요합니다.',
    },
    {
      category: 'QA',
      category_ko: 'QA 검증',
      score: 18,
      max_score: 25,
      feedback: '소셜 로그인 핵심 기능은 완벽히 검증되었으나, 계정 연동 테스트 완료가 필요합니다.',
    },
    {
      category: 'Readiness',
      category_ko: '출시 준비도',
      score: 15,
      max_score: 25,
      feedback: 'CS 대응 시나리오와 FAQ 준비가 필요합니다. 계정 연동 관련 문의가 예상됩니다.',
    },
  ],
  blockers: [],
  recommendations: [
    '계정 연동 테스트 7건 완료',
    '계정 충돌 시 사용자 선택 플로우 확정',
    'CS FAQ 및 대응 매뉴얼 준비',
  ],
  structured_recommendations: [
    {
      title: '계정 연동 테스트 완료',
      description: '기존 회원이 소셜 계정을 연동할 때 발생할 수 있는 모든 시나리오를 검증해야 합니다.',
      priority: 'high',
      category: 'qa',
      action_items: [
        '동일 이메일 소셜 계정 연동 테스트',
        '다중 소셜 계정 연동 테스트',
        '연동 해제 및 재연동 테스트',
        '소셜 계정 삭제 후 재가입 테스트',
      ],
      deadline: '출시 2일 전',
      owner: 'QA팀',
    },
    {
      title: '계정 충돌 처리 확정',
      description: '동일 이메일로 가입된 기존 회원이 소셜 로그인 시도 시 처리 방안을 확정하세요.',
      priority: 'medium',
      category: 'planning',
      action_items: [
        '충돌 시나리오별 처리 방안 문서화',
        '사용자 선택 UI 디자인 확정',
        '본인 인증 절차 연동',
      ],
      deadline: '출시 3일 전',
      owner: '기획팀',
    },
    {
      title: 'CS 대응 준비',
      description: '소셜 로그인 및 계정 연동 관련 문의 증가가 예상됩니다. CS 대응을 준비하세요.',
      priority: 'low',
      category: 'readiness',
      action_items: [
        'FAQ 문서 작성',
        'CS 대응 매뉴얼 작성',
        'CS팀 교육 진행',
      ],
      deadline: '출시 당일',
      owner: 'CS팀',
    },
  ],
  suggested_testcases: SAMPLE_5_TESTCASES_KO,
  context_insights: [
    {
      insight_type: 'service',
      keyword: '소셜 로그인 (카카오, 네이버, Google)',
      version_or_detail: undefined,
      key_changes: [
        'OAuth 2.0 / OIDC 기반 인증 표준 준수 필요',
        '각 플랫폼별 SDK 버전 업데이트 주기 확인 (연 1-2회)',
        '사용자 정보 동의 범위 및 철회 처리 로직 구현 필수',
      ],
      aligned_points: [
        '카카오, 네이버, Google 로그인 연동으로 사용자 가입 편의성 증대',
        '국내 사용자에게 친숙한 소셜 로그인 옵션 제공',
      ],
      gaps: [
        '각 플랫폼 개발자 센터에서 앱 검수 상태 확인 필요',
        '로그인 실패 시 폴백 처리 및 에러 핸들링 검증 필요',
        '토큰 갱신 및 만료 처리 로직 테스트 필요',
      ],
      tips: [
        '각 플랫폼 개발자 문서에서 최신 SDK 버전 확인',
        '테스트 계정으로 전체 로그인 플로우 사전 검증',
        '회원 탈퇴 시 연동 해제 처리 필수',
      ],
    },
    {
      insight_type: 'platform',
      keyword: 'iOS',
      version_or_detail: undefined,
      key_changes: [
        'iOS 18: Control Center 커스터마이징, 다크 모드 아이콘 지원',
        '앱 심사 가이드라인 업데이트 주기 확인 필요',
      ],
      aligned_points: ['소셜 로그인 SDK의 iOS 최신 버전 호환성 확보'],
      gaps: ['iOS 앱 심사 리젝 사유 사전 점검 필요'],
      tips: ['App Store Connect에서 앱 검수 상태 모니터링'],
    },
    {
      insight_type: 'platform',
      keyword: 'Android',
      version_or_detail: undefined,
      key_changes: [
        'Android 14+: 예측 뒤로가기 제스처 지원 필요',
        'targetSdkVersion 요구사항 확인 필요',
      ],
      aligned_points: ['소셜 로그인 SDK의 Android 최신 버전 호환성 확보'],
      gaps: ['Play Store 정책 변경 사항 확인 필요'],
      tips: ['Play Console에서 Pre-launch Report 활용'],
    },
  ],
};

const SAMPLE_5_RESULT_EN: ReleaseDecision = {
  feature_name: 'Social Login Integration and Signup Simplification',
  release_date: '2025-02-10',
  confidence_score: 72,
  status: 'conditional',
  status_label: 'Conditional Release',
  breakdown: [
    {
      category: 'Planning',
      category_ko: '기획 완성도',
      score: 22,
      max_score: 25,
      feedback: 'Conversion rate improvement goal is clear, and user journey is well designed.',
    },
    {
      category: 'Risk',
      category_ko: '리스크 관리',
      score: 17,
      max_score: 25,
      feedback: 'Social login outage response plan exists, but account conflict handling logic needs verification.',
    },
    {
      category: 'QA',
      category_ko: 'QA 검증',
      score: 18,
      max_score: 25,
      feedback: 'Core social login features are fully verified, but account linking tests need to be completed.',
    },
    {
      category: 'Readiness',
      category_ko: '출시 준비도',
      score: 15,
      max_score: 25,
      feedback: 'CS response scenarios and FAQ preparation needed. Account linking inquiries are expected.',
    },
  ],
  blockers: [],
  recommendations: [
    'Complete 7 account linking tests',
    'Finalize user choice flow for account conflicts',
    'Prepare CS FAQ and response manual',
  ],
  structured_recommendations: [
    {
      title: 'Complete Account Linking Tests',
      description: 'All scenarios that may occur when existing members link social accounts must be verified.',
      priority: 'high',
      category: 'qa',
      action_items: [
        'Test same email social account linking',
        'Test multiple social account linking',
        'Test unlinking and re-linking',
        'Test re-signup after social account deletion',
      ],
      deadline: '2 days before release',
      owner: 'QA Team',
    },
    {
      title: 'Finalize Account Conflict Handling',
      description: 'Finalize how to handle when existing members with the same email attempt social login.',
      priority: 'medium',
      category: 'planning',
      action_items: [
        'Document handling for each conflict scenario',
        'Finalize user choice UI design',
        'Integrate identity verification process',
      ],
      deadline: '3 days before release',
      owner: 'Planning Team',
    },
    {
      title: 'Prepare CS Response',
      description: 'An increase in inquiries related to social login and account linking is expected. Prepare CS response.',
      priority: 'low',
      category: 'readiness',
      action_items: [
        'Write FAQ document',
        'Write CS response manual',
        'Conduct CS team training',
      ],
      deadline: 'Release day',
      owner: 'CS Team',
    },
  ],
  suggested_testcases: SAMPLE_5_TESTCASES_EN,
  context_insights: [
    {
      insight_type: 'service',
      keyword: 'Social Login (Kakao, Naver, Google)',
      version_or_detail: undefined,
      key_changes: [
        'OAuth 2.0 / OIDC authentication standard compliance required',
        'Check SDK version update cycle for each platform (1-2x/year)',
        'Implement user consent scope and revocation handling',
      ],
      aligned_points: [
        'Kakao, Naver, Google login integration improves user signup convenience',
        'Provides familiar social login options for users',
      ],
      gaps: [
        'Check app review status at each platform developer center',
        'Verify login failure fallback and error handling',
        'Test token refresh and expiration handling logic',
      ],
      tips: [
        'Check latest SDK version in each platform developer docs',
        'Pre-verify full login flow with test accounts',
        'Handle account unlinking on user deletion',
      ],
    },
    {
      insight_type: 'platform',
      keyword: 'iOS',
      version_or_detail: undefined,
      key_changes: [
        'iOS 18: Control Center customization, dark mode icon support',
        'Check app review guideline update cycle',
      ],
      aligned_points: ['Social login SDK iOS latest version compatibility secured'],
      gaps: ['Pre-check iOS app review rejection reasons'],
      tips: ['Monitor app review status in App Store Connect'],
    },
    {
      insight_type: 'platform',
      keyword: 'Android',
      version_or_detail: undefined,
      key_changes: [
        'Android 14+: Predictive back gesture support required',
        'Check targetSdkVersion requirements',
      ],
      aligned_points: ['Social login SDK Android latest version compatibility secured'],
      gaps: ['Check Play Store policy changes'],
      tips: ['Utilize Pre-launch Report in Play Console'],
    },
  ],
};

// ============================================
// 샘플 데이터 배열 및 랜덤 선택 함수
// ============================================
interface DemoSample {
  input_ko: ReleaseInput;
  input_en: ReleaseInput;
  result_ko: ReleaseDecision;
  result_en: ReleaseDecision;
}

const DEMO_SAMPLES: DemoSample[] = [
  {
    input_ko: SAMPLE_1_INPUT_KO,
    input_en: SAMPLE_1_INPUT_EN,
    result_ko: SAMPLE_1_RESULT_KO,
    result_en: SAMPLE_1_RESULT_EN,
  },
  {
    input_ko: SAMPLE_2_INPUT_KO,
    input_en: SAMPLE_2_INPUT_EN,
    result_ko: SAMPLE_2_RESULT_KO,
    result_en: SAMPLE_2_RESULT_EN,
  },
  {
    input_ko: SAMPLE_3_INPUT_KO,
    input_en: SAMPLE_3_INPUT_EN,
    result_ko: SAMPLE_3_RESULT_KO,
    result_en: SAMPLE_3_RESULT_EN,
  },
  {
    input_ko: SAMPLE_4_INPUT_KO,
    input_en: SAMPLE_4_INPUT_EN,
    result_ko: SAMPLE_4_RESULT_KO,
    result_en: SAMPLE_4_RESULT_EN,
  },
  {
    input_ko: SAMPLE_5_INPUT_KO,
    input_en: SAMPLE_5_INPUT_EN,
    result_ko: SAMPLE_5_RESULT_KO,
    result_en: SAMPLE_5_RESULT_EN,
  },
];

// 랜덤 샘플 인덱스 (세션 당 한 번만 선택)
let selectedSampleIndex: number | null = null;

function getRandomSampleIndex(): number {
  if (selectedSampleIndex === null) {
    selectedSampleIndex = Math.floor(Math.random() * DEMO_SAMPLES.length);
  }
  return selectedSampleIndex;
}

// 언어에 따른 데모 데이터 선택 함수
export function getDemoInput(lang: 'ko' | 'en'): ReleaseInput {
  const sample = DEMO_SAMPLES[getRandomSampleIndex()];
  return lang === 'ko' ? sample.input_ko : sample.input_en;
}

export function getDemoResult(lang: 'ko' | 'en'): ReleaseDecision {
  const sample = DEMO_SAMPLES[getRandomSampleIndex()];
  return lang === 'ko' ? sample.result_ko : sample.result_en;
}

// 새로운 샘플을 강제로 선택하고 싶을 때 사용
export function resetDemoSample(): void {
  selectedSampleIndex = null;
}

// 하위 호환성을 위해 기존 export도 유지
export const DEMO_INPUT_KO = SAMPLE_1_INPUT_KO;
export const DEMO_INPUT_EN = SAMPLE_1_INPUT_EN;
export const DEMO_RESULT_KO = SAMPLE_1_RESULT_KO;
export const DEMO_RESULT_EN = SAMPLE_1_RESULT_EN;
